import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { HeaderMessageConfig } from '../../../common/interfaces';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'vsf-header-messages',
  templateUrl: './page-header-messages.component.html',
  styleUrls: ['./page-header-messages.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageHeaderMessagesComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() config: HeaderMessageConfig | null = null;
  @Input() textColor = 'white';
  @Input() backgroundColor = 'black';
  @ViewChild('textEl', { static: false }) textElRef!: ElementRef<HTMLElement>;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private changeDetector: ChangeDetectorRef,
  ) {}

  private readonly DEFAULT_MARQUEE_DURATION_SECONDS = 30;
  private readonly DEFAULT_AUTO_SWITCH_INTERVAL_SECONDS = 3;
  private readonly DEFAULT_ROW_ROTATION_SPEED_SECONDS = 20;
  private readonly DEFAULT_ROW_ROTATION_CHARACTERS = 100;

  currentMessageIndex = 0;
  currentMessage = '';
  intervalId: any;
  shouldMarquee = false;
  marqueeDuration = this.DEFAULT_MARQUEE_DURATION_SECONDS;

  ngOnInit() {
    if (!this.config || !this.config.messageList?.length) {
      return;
    }
    this.currentMessage = this.config.messageList[this.currentMessageIndex];
    if (this.config.switchType === 'auto' && isPlatformBrowser(this.platformId)) {
      this.startAutoSwitch();
    }
  }

  ngAfterViewInit() {
    if(isPlatformBrowser(this.platformId)) {
      this.resetMarquee();
    }
  }

  ngOnDestroy() {
    this.clearAutoSwitch();
  }

  private startAutoSwitch() {
    this.clearAutoSwitch();
    this.intervalId = setInterval(() => {
      this.nextMessage();
    }, (this.config?.autoSwitchInterval || this.DEFAULT_AUTO_SWITCH_INTERVAL_SECONDS) *1000);
  }

  private clearAutoSwitch() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private nextMessage() {
    if (!this.config?.messageList?.length) return;

    if (this.config.displayOrder === 'sequence') {
      this.currentMessageIndex =
        (this.currentMessageIndex + 1) % this.config.messageList.length;
    } else if (this.config.displayOrder === 'random') {
      this.currentMessageIndex = this.getRandomIndexExcluding(
        this.currentMessageIndex,
        this.config.messageList.length
      );
    }
    this.currentMessage = this.config.messageList[this.currentMessageIndex];
    this.resetMarquee();
  }

  /**
   * Move to the previous message (useful if you want a "prev" button).
   */
  prevMessage() {
    if (!this.config || !this.config.messageList?.length) {
      return;
    }
    if (this.config.displayOrder === 'sequence') {
      // Move index in a circular manner
      this.currentMessageIndex =
        (this.currentMessageIndex - 1 + this.config.messageList.length) %
        this.config.messageList.length;
    } else if (this.config.displayOrder === 'random') {
      this.currentMessageIndex = this.getRandomIndexExcluding(
        this.currentMessageIndex,
        this.config.messageList.length
      );
    }
    this.currentMessage = this.config.messageList[this.currentMessageIndex];
    this.resetMarquee();
  }

  onNextClick() {
    if (this.config?.switchType === 'auto') {
      this.clearAutoSwitch();
    }
    this.nextMessage();
    if (this.config?.switchType === 'auto') {
      this.startAutoSwitch();
    }
  }

  onPrevClick() {
    if (this.config?.switchType === 'auto') {
      this.clearAutoSwitch();
    }
    this.prevMessage();
    if (this.config?.switchType === 'auto') {
      this.startAutoSwitch();
    }
  }

  private getRandomIndexExcluding(excludeIndex: number, length: number): number {
    if (length <= 1) return 0;
    let newIndex: number;
    do {
      newIndex = Math.floor(Math.random() * length);
    } while (newIndex === excludeIndex);
    return newIndex;
  }

  private resetMarquee() {
    this.shouldMarquee = false;
    this.changeDetector.markForCheck();
    setTimeout(() => this.updateMarqueeState(), 0);
  }

  private updateMarqueeState() {
    if (!this.textElRef) return;

    const textLength = this.currentMessage.length;
    this.shouldMarquee = textLength > this.DEFAULT_ROW_ROTATION_CHARACTERS;
    if (this.shouldMarquee) {
      const baseDuration = this.DEFAULT_ROW_ROTATION_SPEED_SECONDS;
      const ratio = textLength / this.DEFAULT_ROW_ROTATION_CHARACTERS;
      this.marqueeDuration = ratio * baseDuration;
    } else {
      this.marqueeDuration = 0;
    }
    this.changeDetector.markForCheck();
  }
}
